import { Action } from '@nextforce/api'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useServer } from 'app/server/context'
import monthlyEarningsKeys from 'home/monthly_summary/queries/shared/keys'
import { produce } from 'immer'
import scheduleKeys from 'schedule/queries/shared/keys'
import { SingleSelectSetting } from 'settings/types'
import { performAction } from 'shared/queries/perform_action'
import storesKeys from 'stores/queries/shared/keys'
import { THEME_SETTING_ID } from './settings'
import { getKey } from './shared/keys'
import { SettingsSource, UserSettingsData } from './shared/types'

type Variables = Action

export function usePerformSettingAction(source: SettingsSource) {
  const { server } = useServer()
  const client = useQueryClient()
  const settingsCacheKey = getKey(source)

  return useMutation({
    mutationFn: mutateSetting,
    onMutate: async data => {
      if (source.type !== 'user') {
        return
      }

      const isThemeSetting =
        data.params?.preferences[0].name === THEME_SETTING_ID

      if (!isThemeSetting) {
        return
      }

      const settingsCachedData =
        client.getQueryData<UserSettingsData>(settingsCacheKey)

      if (!settingsCachedData) {
        return
      }

      const themeSettingIndex = settingsCachedData.settings.findIndex(
        setting => setting.id === THEME_SETTING_ID
      )

      if (themeSettingIndex === -1) {
        return
      }

      await client.cancelQueries({ queryKey: settingsCacheKey })

      client.setQueryData<UserSettingsData>(
        settingsCacheKey,
        produce(settingsCachedData, updatedSettings => {
          const themeSetting = updatedSettings.settings[
            themeSettingIndex
          ] as SingleSelectSetting
          themeSetting.selectedId = data.params?.preferences[0].values[0]
        })
      )
    },
    onSuccess: async () => {
      client.invalidateQueries({ queryKey: storesKeys.all }).catch(e => null)
      client.invalidateQueries({ queryKey: scheduleKeys.all }).catch(e => null)
      client
        .invalidateQueries({ queryKey: monthlyEarningsKeys.all })
        .catch(e => null)
      return client.invalidateQueries({ queryKey: settingsCacheKey })
    },
    onError: async () => {
      return client.invalidateQueries({ queryKey: settingsCacheKey })
    },
  })

  async function mutateSetting(action: Variables) {
    return performAction(server, action)
  }
}
